import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Links,
	Meta,
	Outlet,
	redirect,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
// Placeholder — swap for the real staff-console dog artwork (see host-aware
// favicon below). Shown only on the admin/staff host.
import dogFaviconAssetUrl from './assets/favicons/dog.png'
import dogIconAssetUrl from './assets/favicons/dog-64.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { EnvBanner } from './components/env-banner.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { BRAND_BY_SURFACE } from './utils/brand.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import {
	type HostSurface,
	hostSurfaceRedirect,
	resolveHostSurface,
} from './utils/host-surface.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { FORCED_THEME } from './utils/theme-mode.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

// Host-aware favicon (issue 47 surfaces). The staff/admin console gets its own
// mark; every other surface — customer, prescriber, internal *.fly.dev — keeps
// the Dimple brand favicon. Keyed by the resolved HostSurface so adding a
// per-surface icon later is a one-line change.
type FaviconSet = {
	icon: string
	iconType: string
	appleTouchIcon: string
	/** Cold-fallback /favicon.ico — emitted only on surfaces that want it (brand,
	 * NOT staff). A sized .ico out-selects an unsized PNG in Chrome, so emitting it
	 * on the staff surface would shadow the dog. */
	coldIco?: string
}

const BRAND_FAVICON: FaviconSet = {
	icon: faviconAssetUrl,
	iconType: 'image/svg+xml',
	appleTouchIcon: appleTouchIconAssetUrl,
	coldIco: '/favicon.ico',
}

const FAVICONS_BY_SURFACE: Record<HostSurface, FaviconSet> = {
	staff: {
		// Lean 64px dog for the tab; the full 512px dog stays for apple-touch. No
		// coldIco on purpose — see the FaviconSet.coldIco note above.
		icon: dogIconAssetUrl,
		iconType: 'image/png',
		appleTouchIcon: dogFaviconAssetUrl,
	},
	customer: BRAND_FAVICON,
	prescriber: BRAND_FAVICON,
	internal: BRAND_FAVICON,
}

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// The tab icon (host-aware) + its /favicon.ico cold fallback are emitted in
		// <Document>, so the .ico is sent on brand surfaces only and never shadows
		// the staff dog. Do NOT re-add a /favicon.ico link here.
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	// Fallback title for routes that set none of their own. Host-aware because
	// this one function covers every surface; a route that knows its own
	// audience should just set its own title (see app/utils/brand.ts).
	const brand = BRAND_BY_SURFACE[data?.hostSurface ?? 'internal']
	return [
		{ title: data ? brand.name : `Error | ${brand.name}` },
		{ name: 'description', content: brand.tagline },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	// Host-based route gating (issue 47): on a branded customer/prescriber host,
	// paths outside that surface redirect to its neutral landing. Inert until the
	// *_BASE_URL env vars are set (unmatched host → 'internal' → unrestricted).
	// Resource/API routes bypass the root loader, so /api/* and webhooks (which
	// only hit the internal *.fly.dev host) are never gated here.
	const surfaceRedirect = hostSurfaceRedirect(request)
	if (surfaceRedirect) throw redirect(surfaceRedirect)

	// Which branded surface this host is — drives the host-aware favicon and the
	// brand on the two things that are shared across surfaces: the fallback
	// <title> and the neutral `/` landing.
	const hostSurface = resolveHostSurface(request)

	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			hostSurface,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	hostSurface = 'internal',
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	hostSurface?: HostSurface
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	const favicon = FAVICONS_BY_SURFACE[hostSurface]
	return (
		<html
			lang="en"
			className={`${theme} h-full overflow-x-hidden scroll-smooth`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				{/* Host-aware tab icon. Staff console → its own dog mark; other
				    surfaces → Dimple brand. The /favicon.ico cold fallback is
				    emitted here (not in <Links />) and ONLY when a surface opts in
				    via coldIco, so it is never sent on staff — a sized .ico
				    out-selects the unsized dog PNG in Chrome (why the dog was
				    missing). */}
				<link rel="icon" type={favicon.iconType} href={favicon.icon} />
				<link rel="apple-touch-icon" href={favicon.appleTouchIcon} />
				{favicon.coldIco ? (
					<link rel="icon" href={favicon.coldIco} sizes="48x48" />
				) : null}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				<EnvBanner
					appEnv={env.APP_ENV as 'development' | 'staging' | 'production'}
				/>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	// FORCED_THEME pins every surface (utils/theme-mode.ts); when it's null the
	// app honours the `en_theme` cookie, falling back to the OS client hint. The
	// hook is called unconditionally either way — hook order can't depend on a
	// constant. This class is what locks the theme: every token and every `dark:`
	// utility keys off it.
	const resolvedTheme = useOptionalTheme()
	const theme = FORCED_THEME ?? resolvedTheme
	return (
		<Document
			nonce={nonce}
			theme={theme}
			env={data?.ENV}
			hostSurface={data?.hostSurface}
		>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	// Same pin as `Layout` — sonner picks its own palette, so a forced theme has
	// to reach the toast too or it renders dark on a light page.
	const resolvedTheme = useTheme()
	const theme = FORCED_THEME ?? resolvedTheme
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col">
				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
